/**
 * Navigation Skeleton Components
 *
 * Loading placeholders for navigation areas while CMS menus load.
 * Uses shimmer animation via the Skeleton component.
 */

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Sidebar skeleton - shows placeholder for sidebar navigation
 * Mimics the grouped menu structure with section headers
 */
export function SidebarSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Group 1: Core - 6 items */}
      <div className="space-y-1">
        <Skeleton className="h-3 w-12 mb-2" /> {/* Section header */}
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[90%]" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[85%]" />
      </div>

      {/* Group 2: CRM - 4 items */}
      <div className="space-y-1">
        <Skeleton className="h-3 w-10 mb-2" /> {/* Section header */}
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[95%]" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[88%]" />
      </div>

      {/* Group 3: Communication - 4 items */}
      <div className="space-y-1">
        <Skeleton className="h-3 w-24 mb-2" /> {/* Section header */}
        <Skeleton className="h-8 w-[90%]" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[85%]" />
        <Skeleton className="h-8 w-full" />
      </div>

      {/* Group 4: Content - 5 items */}
      <div className="space-y-1">
        <Skeleton className="h-3 w-16 mb-2" /> {/* Section header */}
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[92%]" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-[88%]" />
        <Skeleton className="h-8 w-full" />
      </div>
    </div>
  )
}

/**
 * Header navigation skeleton - horizontal items for desktop header
 */
export function HeaderNavSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-4', className)}>
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-8 w-24" />
      <Skeleton className="h-8 w-16" />
      <Skeleton className="h-8 w-20" />
    </div>
  )
}

/**
 * Footer skeleton - 3-column footer link layout
 */
export function FooterSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-3 gap-8', className)}>
      {/* Column 1 */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-28" />
      </div>

      {/* Column 2 */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 w-28" />
      </div>

      {/* Column 3 */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  )
}

/**
 * User menu skeleton - dropdown menu items
 */
export function UserMenuSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-1 p-1', className)}>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <div className="my-1 h-px bg-border" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <div className="my-1 h-px bg-border" />
      <Skeleton className="h-8 w-full" />
    </div>
  )
}

/**
 * Sidebar error state - shows when menu fails to load
 */
export function SidebarError({
  error,
  onRetry,
}: {
  error: Error | null
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <p className="text-sm text-muted-foreground mb-2">Failed to load menu</p>
      {error && (
        <p className="text-xs text-destructive mb-4">{error.message}</p>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-sm text-primary hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  )
}

/**
 * Empty menu state - shows when no menu is configured
 */
export function EmptyMenuMessage() {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <p className="text-sm text-muted-foreground">No menu configured</p>
      <p className="text-xs text-muted-foreground mt-1">
        Configure menus in CMS settings
      </p>
    </div>
  )
}
